// SCRUM-451: the Event Ops photo filter. Statuses (the four match buckets)
// combine with OR, sales tags with OR, and the two groups with AND — the
// API builds the same query (api ops-photo-filter.ts). No status selected
// is "Photos": every photo, or with an athlete her superset. Deleted stands
// alone. Kept out of EventOps.jsx so it is testable without axios.

export const STATUSES = ['matched', 'assigned', 'review', 'suppressed'];
export const TAGS = ['incart', 'purchased'];
export const ALL_PHOTOS = Object.freeze({ deleted: false, statuses: [], tags: [] });
export const DELETED = Object.freeze({ deleted: true, statuses: [], tags: [] });

const STATUS_LABEL = { matched: 'Rekognized', assigned: 'Manual', review: 'Review', suppressed: 'Suppressed' };
const TAG_LABEL = { incart: 'In carts', purchased: 'Purchased' };

// Toggle x in list, keeping the cards' order so params/titles are stable.
const toggle = (list, x, order) =>
  order.filter((y) => (y === x ? !list.includes(x) : list.includes(y)));

const live = (f) => (f.deleted ? ALL_PHOTOS : f);

export const toggleStatus = (f, s) => {
  const b = live(f);
  return { deleted: false, statuses: toggle(b.statuses, s, STATUSES), tags: b.tags };
};
export const toggleTag = (f, t) => {
  const b = live(f);
  return { deleted: false, statuses: b.statuses, tags: toggle(b.tags, t, TAGS) };
};
export const clearStatuses = (f) => ({ deleted: false, statuses: [], tags: live(f).tags });
export const isPhotosActive = (f) => !f.deleted && f.statuses.length === 0;

// Which count cards show the active highlight. None in parent view: the
// grid is the family gallery, not the selected filter (a card click leaves
// parent view and applies itself).
export const activeCards = (f, { asParent }) => {
  if (asParent) return { photos: false, statuses: [], tags: [], deleted: false };
  return {
    photos: isPhotosActive(f),
    statuses: f.deleted ? [] : f.statuses,
    tags: f.deleted ? [] : f.tags,
    deleted: f.deleted,
  };
};

export const filterParams = (f, { asParent }) => {
  if (asParent) return { view: 'attendee' };
  if (f.deleted) return { view: 'deleted' };
  return {
    view: 'all',
    statuses: f.statuses.join(',') || undefined,
    tags: f.tags.join(',') || undefined,
  };
};

// "As parent sees it" shows the family's gallery whatever is selected, so
// the header says so instead of naming the operator's filter.
export const filterTitle = (f, hasAthlete, asParent = false) => {
  if (asParent) return 'As the parent sees it';
  if (f.deleted) return 'Deleted photos';
  const tags = f.tags.map((t) => TAG_LABEL[t]).join(' or ');
  if (!f.statuses.length) {
    if (tags) return tags;
    return hasAthlete ? 'All photos with any match — auto, manual, review, suppressed' : 'All photos';
  }
  const statuses = f.statuses.map((s) => STATUS_LABEL[s]).join(' + ');
  return tags ? `${statuses} · ${tags}` : statuses;
};

const ROSTER_STATUS = {
  matched: (r) => r.matchCount > 0,
  assigned: (r) => !!r.hasManual,
  review: (r) => r.reviewCount > 0,
  suppressed: (r) => r.suppressedCount > 0,
};
const ROSTER_TAG = {
  incart: (r) => !!r.parentInCart,
  purchased: (r) => !!r.parentPurchase,
};

// The roster follows the filter; the selected athlete always stays so the
// selection can't vanish. Deleted keeps everyone.
export const rosterMatchesFilter = (r, f, attendeeId) => {
  if (r.attendeeId === attendeeId || f.deleted) return true;
  const okStatus = !f.statuses.length || f.statuses.some((s) => ROSTER_STATUS[s](r));
  const okTag = !f.tags.length || f.tags.some((t) => ROSTER_TAG[t](r));
  return okStatus && okTag;
};

// Suppressed chips (and the Restore action) show only where a suppressed
// match can belong to the set: Review/Suppressed selected, or an athlete's
// superset (SCRUM-338/408).
export const showSuppressedChips = (f, attendeeId) =>
  !f.deleted &&
  (f.statuses.includes('suppressed') || f.statuses.includes('review') || (f.statuses.length === 0 && !!attendeeId));

export const keepOnAthleteChange = (f) => (f.deleted ? ALL_PHOTOS : f);
